import React, { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import { TableProperties, LayoutGrid } from "lucide-react";
import WsrReportsTableView from "./WsrReportsTableView";
import WsrReportsCardsView from "./WsrReportsCardsView";
import DropDown from "../controls/dropdown/DropdownFluent";
import styles from "./WsrReportsView.module.scss";
import { Tooltip } from "@fluentui/react-components";

const PAGE_SIZE = 15;
const SCROLL_THRESHOLD = 100;

const SORT_OPTIONS = [
  { value: "Period desc", name: "Period (Newest first)" },
  { value: "Period asc", name: "Period (Oldest first)" },
  { value: "ConfirmedAt desc", name: "Confirmed (Newest first)" },
  { value: "ConfirmedAt asc", name: "Confirmed (Oldest first)" },
];

const WsrReportsView = ({ refId, fetchReports }) => {
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [viewMode, setViewMode] = useState("cards");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortValue, setSortValue] = useState(SORT_OPTIONS[0].value);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestSeqRef = useRef(0);
  const scrollRef = useRef(null);

  const debouncedSearch = useRef(debounce((value) => setSearchTerm(value), 400)).current;

  // Every fetch gets a sequence number - only the response matching the latest dispatched
  // request is applied. This guards against duplicate/appended entries from overlapping
  // requests (rapid tab switching, dev-mode double effects, fast search typing, etc).
  const runFetch = useCallback(
    async (page, isReset) => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        const response = await fetchReports({
          id: refId,
          pageNumber: page,
          pageSize: PAGE_SIZE,
          searchTerm,
          orderBy: sortValue,
        });
        if (seq !== requestSeqRef.current) return;
        const data = response?.data || [];
        const pagination = response?.headers?.["x-pagination"]
          ? JSON.parse(response.headers["x-pagination"])
          : null;
        setItems((prev) => (isReset ? data : [...prev, ...data]));
        setHasMore(!!pagination?.HasNext);
        setPageNumber(page);
      } catch (e) {
        if (seq === requestSeqRef.current) {
          setItems([]);
          setHasMore(false);
        }
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    },
    [fetchReports, refId, searchTerm, sortValue]
  );

  // Reset + refetch whenever search or sort changes (also covers the initial mount).
  useEffect(() => {
    runFetch(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, sortValue]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    runFetch(pageNumber + 1, false);
  }, [loading, hasMore, pageNumber, runFetch]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handlePreview = (report) => window.open(report.url, "_blank", "noopener,noreferrer");
  const handleDownload = (report) => {
    // downloadUrl is presigned with a Content-Disposition: attachment override, so S3 itself
    // forces the save-as dialog on navigation - a plain fetch()+blob wouldn't work here since
    // the reports bucket has no CORS policy (it's private, app-only).
    const link = document.createElement("a");
    link.href = report.downloadUrl || report.url;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sortValue)?.name;

  return (
    <div className={styles.wsrReportsView}>
      <div className={styles.toolbar}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by period..."
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            debouncedSearch(e.target.value);
          }}
        />
        <DropDown
          options={SORT_OPTIONS}
          onChange={setSortValue}
          defaultVal={currentSortLabel}
          showLabel={false}
          placeholder="Sort by"
          StyleClass={styles.sortDropdown}
        />
        <div className={styles.toolbarSpacer} />
        <div className="resourcesTabToggleContainer">
          <Tooltip content={"Cards view"} relationship="label">
            <button
              type="button"
              title="Cards view"
              className={`resourcesTabToggleButton ${viewMode === "cards" ? "active" : ""}`}
              onClick={() => setViewMode("cards")}
            >
              <LayoutGrid size={14} />
            </button>
          </Tooltip>
          <Tooltip content={"Table view"} relationship="label">
            <button
              type="button"
              title="Table view"
              className={`resourcesTabToggleButton ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              <TableProperties size={14} />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className={styles.wsrReportsScroller} ref={scrollRef}>
        {viewMode === "table" ? (
          <WsrReportsTableView
            reports={items}
            loading={loading}
            settings={settings}
            onPreview={handlePreview}
            onDownload={handleDownload}
          />
        ) : (
          <WsrReportsCardsView
            reports={items}
            loading={loading}
            settings={settings}
            onPreview={handlePreview}
            onDownload={handleDownload}
          />
        )}
      </div>
    </div >
  );
};

export default WsrReportsView;
